import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Trash2, Plus, ExternalLink } from "lucide-react";

export default function SupplierProfile() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    company_name: "",
    name: "",
    phone: "",
    country: "",
    bio: "",
    video_url: "",
    certifications: [],
    gallery_urls: [],
  });
  const [newCert, setNewCert] = useState("");
  const [newImage, setNewImage] = useState("");

  useEffect(() => {
    api.get("/me/profile")
      .then((r) => {
        const u = r.data || {};
        setForm({
          company_name: u.company_name || "",
          name: u.name || "",
          phone: u.phone || "",
          country: u.country || "",
          bio: u.bio || "",
          video_url: u.video_url || "",
          certifications: u.certifications || [],
          gallery_urls: u.gallery_urls || [],
        });
      })
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/me/profile", form);
      toast.success("Đã lưu thông tin công ty");
    } catch (e) {
      toast.error("Lưu thất bại — vui lòng thử lại");
    } finally {
      setSaving(false);
    }
  };

  const addCert = () => {
    const v = newCert.trim();
    if (!v) return;
    setForm((f) => ({ ...f, certifications: [...f.certifications, v] }));
    setNewCert("");
  };

  const removeCert = (i) => {
    setForm((f) => ({ ...f, certifications: f.certifications.filter((_, idx) => idx !== i) }));
  };

  const addImage = () => {
    const v = newImage.trim();
    if (!v) return;
    setForm((f) => ({ ...f, gallery_urls: [...f.gallery_urls, v] }));
    setNewImage("");
  };

  const removeImage = (i) => {
    setForm((f) => ({ ...f, gallery_urls: f.gallery_urls.filter((_, idx) => idx !== i) }));
  };

  const moveImage = (from, to) => {
    if (to < 0 || to >= form.gallery_urls.length) return;
    setForm((f) => {
      const arr = [...f.gallery_urls];
      const [item] = arr.splice(from, 1);
      arr.splice(to, 0, item);
      return { ...f, gallery_urls: arr };
    });
  };

  if (loading) {
    return <DashboardLayout title="Company profile"><div className="text-muted-foreground">Loading…</div></DashboardLayout>;
  }

  return (
    <DashboardLayout
      title="Company profile"
      actions={
        <Button onClick={save} disabled={saving} data-testid="save-profile-btn">
          {saving ? "Saving…" : "Save changes"}
        </Button>
      }
    >
      <div className="max-w-3xl space-y-8">
        {/* Basics */}
        <section className="bg-white border border-border rounded-lg p-6 space-y-4" data-testid="section-basics">
          <div className="font-heading font-bold text-secondary text-lg">Basic info</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Company name</Label>
              <Input
                data-testid="input-company-name"
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
              />
            </div>
            <div>
              <Label>Contact name</Label>
              <Input
                data-testid="input-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                data-testid="input-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
            <div>
              <Label>Country</Label>
              <Input
                data-testid="input-country"
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
              />
            </div>
          </div>
          <div>
            <Label>Company bio</Label>
            <Textarea
              data-testid="input-bio"
              rows={5}
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              placeholder="Tell buyers about your brand, origin, values, certifications, and what makes you unique."
            />
            <p className="text-xs text-muted-foreground mt-1">Shown on your public storefront (/suppliers/&lt;your-id&gt;).</p>
          </div>
        </section>

        {/* Certifications */}
        <section className="bg-white border border-border rounded-lg p-6 space-y-4" data-testid="section-certs">
          <div className="font-heading font-bold text-secondary text-lg">Certifications</div>
          <div className="flex flex-wrap gap-2">
            {form.certifications.map((c, i) => (
              <span
                key={i}
                data-testid={`cert-tag-${i}`}
                className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary text-xs font-heading font-black uppercase tracking-wider px-3 py-1.5"
              >
                {c}
                <button
                  onClick={() => removeCert(i)}
                  className="hover:text-red-600"
                  data-testid={`cert-remove-${i}`}
                  aria-label="Remove"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </span>
            ))}
            {form.certifications.length === 0 && <div className="text-sm text-muted-foreground">No certifications yet.</div>}
          </div>
          <div className="flex gap-2">
            <Input
              data-testid="input-new-cert"
              placeholder="e.g. HACCP, Direct Trade, EU Food Safety"
              value={newCert}
              onChange={(e) => setNewCert(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCert())}
            />
            <Button variant="outline" onClick={addCert} data-testid="add-cert-btn">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
        </section>

        {/* Gallery */}
        <section className="bg-white border border-border rounded-lg p-6 space-y-4" data-testid="section-gallery">
          <div className="flex items-start justify-between">
            <div>
              <div className="font-heading font-bold text-secondary text-lg">Behind-the-scenes gallery</div>
              <p className="text-xs text-muted-foreground mt-1">Paste image URLs (JPG/PNG/WEBP). Up to 8 photos will show on your public storefront.</p>
            </div>
          </div>

          <div className="flex gap-2">
            <Input
              data-testid="input-new-image"
              placeholder="https://example.com/photo.jpg"
              value={newImage}
              onChange={(e) => setNewImage(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addImage())}
            />
            <Button variant="outline" onClick={addImage} data-testid="add-image-btn">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>

          {form.gallery_urls.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {form.gallery_urls.map((url, i) => (
                <div key={i} data-testid={`gallery-item-${i}`} className="relative group aspect-square rounded-md overflow-hidden bg-muted ring-1 ring-border">
                  <img src={url} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.opacity = "0.3"; }} />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                    <div className="flex gap-1">
                      <button onClick={() => moveImage(i, i - 1)} disabled={i === 0} className="text-white text-xs bg-white/20 hover:bg-white/30 rounded px-2 py-1 disabled:opacity-30" data-testid={`gallery-up-${i}`}>← up</button>
                      <button onClick={() => moveImage(i, i + 1)} disabled={i === form.gallery_urls.length - 1} className="text-white text-xs bg-white/20 hover:bg-white/30 rounded px-2 py-1 disabled:opacity-30" data-testid={`gallery-down-${i}`}>down →</button>
                    </div>
                    <button onClick={() => removeImage(i)} className="text-white text-xs bg-red-600/80 hover:bg-red-600 rounded px-3 py-1 flex items-center gap-1" data-testid={`gallery-remove-${i}`}>
                      <Trash2 className="w-3 h-3" /> Remove
                    </button>
                  </div>
                  <div className="absolute top-1 left-1 bg-white/90 text-secondary text-[10px] font-heading font-black px-1.5 py-0.5 rounded">
                    {i + 1}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground py-3">No gallery photos yet. Add up to 8 URLs.</div>
          )}
        </section>

        {/* Video */}
        <section className="bg-white border border-border rounded-lg p-6 space-y-3" data-testid="section-video">
          <div className="font-heading font-bold text-secondary text-lg">Brand video (optional)</div>
          <p className="text-xs text-muted-foreground">YouTube, Vimeo or a direct MP4/WebM URL. Leave empty to hide.</p>
          <Input
            data-testid="input-video-url"
            placeholder="https://www.youtube.com/watch?v=…"
            value={form.video_url}
            onChange={(e) => setForm({ ...form, video_url: e.target.value })}
          />
          {form.video_url && (
            <a href={form.video_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="preview-video-link">
              Preview link <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </section>

        <div className="pt-2">
          <Button onClick={save} disabled={saving} data-testid="save-profile-btn-bottom" className="w-full md:w-auto">
            {saving ? "Saving…" : "Save all changes"}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
